import {useEffect, useLayoutEffect, useState} from "react";
import {GetAllServiceType} from "../../../../../reduxToolkit/ServiceType/ServiceTypeApi";
import {toast} from "react-toastify";
import routes from "../../../../../Routes/routes";
import {Supplier} from "../../../../../models/Supplier";
import {CreateEmployeeSupplier} from "../../../../../reduxToolkit/EmployeeProviders/EmployeeProvidersApi";
import {getPhotoPicker} from '../../../../../components/PhotoPicker/SubComponent/Models/Models';
import {errorMsg, isEmailFormat, isPhoneFormat, isValidLengthDocumentId} from '../../../../../Apis/functions';
import {STEPS_PROVIDER_CREATE, TABS_PROVIDER} from "../../../Model/Model";

/**
 * @file EmployeeFlowSupplierCreateApp.jsx
 * @description Custom hook managing the form state and creation logic for a supplier's employee
 * within the employee-supplier onboarding flow.
 *
 * @Email: azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @JobTitle: Full Stack Developer
 */


/**
 * Hook that encapsulates the logic for creating a supplier employee.
 *
 * Handles:
 * - Fetching service types
 * - Validating form input
 * - Managing local state for user and image preview
 * - Submitting data to backend
 *
 * @param {Object} props - Component props
 * @param {Function} props.t - i18n translation function
 * @param {Function} props.dispatch - Redux dispatch function
 * @param {Function} props.navigate - Navigation function from react-router
 * @param {Function} props.getUserFullInformation - Function to retrieve user data
 * @param {string|null} props.previewImage - URL of the image preview
 * @param {Function} props.setPreviewImage - Setter for previewImage
 * @param {boolean} props.showModal - Flag for modal visibility
 * @param {Function} props.setShowModal - Setter for showModal
 * @param {File|null} props.imageFile - Uploaded image file
 * @param {Function} props.setImageFile - Setter for imageFile
 * @param {boolean} props.emptyFields - Flag for form validation state
 * @param {Function} props.setEmptyFields - Setter for emptyFields
 * @param {boolean} props.isValidEmail - Validation flag for email
 * @param {boolean} props.isValidPhoneNumber - Validation flag for phone number
 * @param {boolean} props.isValidDob - Validation flag for date of birth
 * @param {string|number} props.contractorItemId - Contractor item identifier
 * @param {string} props.companyName - Company name of the supplier
 *
 * @returns {Object} Object containing state and handler functions used in the form
 */
export const EmployeeFlowSupplierCreateApp = (
    {
        t,
        dispatch,
        navigate,
        setEmptyFields,
        isValidDob,
        photoPicker,
        setPhotoPicker,
        permission,
        setIsValidEmail,
        setIsValidPhoneNumber,
        isValidPassword,
        setSecretModal,
        setSecretKey,
        setGeneratedPassword,
        passwordGenerator,
        setSteps, 
        isValidDocumentId,
        setIsValidDocumentId,

    }
) => {

    const [getAllServiceType, setGetAllServiceType] = useState([])
    const [user, setUser] = useState({...Supplier});


    useEffect(() => {
        dispatch(GetAllServiceType()).then(({payload: {data: {data}}}) => {
            setGetAllServiceType(data)
        });
    }, [dispatch])

    useLayoutEffect(() => {
        setSteps([t("company_details"), t("manager_details"), t("add_photo"), t("summary")]);
    }, [permission, t]);


    /**
     * Resets the form fields to their initial state.
     */
    const resetForm = (step) => {
        if(step === STEPS_PROVIDER_CREATE.COMPANY_DETAILS){
            setUser({...user.clearCompanyDetails()});
        }
        if(step === STEPS_PROVIDER_CREATE.MANAGER_DETAILS){
            setUser({...user.clearManagerDetails()});
        }

        setEmptyFields(false);
    };

    /**
     * Validates the form fields and triggers the supplier creation if valid.
     * Shows a warning toast if required fields are missing or invalid.
     */
    const FormHandler = () => {
        const emailOk = isEmailFormat(user.email);
        const phoneOk = isPhoneFormat(user.phoneNumber);
        const docummentIdOk =  isValidLengthDocumentId(user.documentId)

        if (!user.isValid() ||
            !isValidDob ||
            !phoneOk ||
            !emailOk ||
            !isValidPassword
        ) {
            toast.warn(t('please_fill_all_fields'));
            setEmptyFields(true);
            setIsValidEmail(emailOk);
            setIsValidPhoneNumber(phoneOk);
            setIsValidDocumentId(docummentIdOk)
        } else {
            setEmptyFields(false);
            createSupplier();
        }
    };

    /**
     * Dispatches the action to create a supplier employee using form data and image file.
     * Displays success or error feedback using toast notifications.
     */
    const createSupplier = () => {
        let imageFile = getPhotoPicker(photoPicker);
        const addPw = user?.password?.trim() !== '' && user?.password !== null;
        const password = addPw ? user.password : passwordGenerator();
        dispatch(CreateEmployeeSupplier({
            data: user.toCreateUpdate(password),
            file: imageFile
        })).then(({payload: {data: {data, success, code}}}) => {
            if (success) {
                setSecretModal(true);
                setSecretKey(data?.secret);
                setGeneratedPassword(addPw ? null : password );
                toast.success(t('has_been_created_successfully'));
                setEmptyFields(false);
            }else{
                errorMsg(code)
            }
        });
    }

    const handleValidateSteps = (step) => {
        switch (step) {
            case STEPS_PROVIDER_CREATE.COMPANY_DETAILS: {
                if (!user.isValidCompanyDetails() ){
                    setEmptyFields(true);
                    return false;
                }
                setEmptyFields(false);
                return true;
            }
            case STEPS_PROVIDER_CREATE.MANAGER_DETAILS: {
                const emailOk = isEmailFormat(user.email);
                const phoneOk = isPhoneFormat(user.phoneNumber);
                const docummentIdOk =  isValidLengthDocumentId(user.documentId)

                if (!user.isValidManagerDetails() ||
                    !isValidDob ||
                    !isValidPassword ||
                    !phoneOk ||
                    !emailOk ||
                    !docummentIdOk
                ){
                    setIsValidEmail(emailOk);
                    setIsValidPhoneNumber(phoneOk);
                    setIsValidDocumentId(docummentIdOk);
                    setEmptyFields(true);
                    return false;
                }
                setEmptyFields(false);
                return true;
            }
            default:
                return true;
        }
    }
    const handleOnBack = () => {
        navigate(routes.suppliers.main, {
            state: {
                currentTab:TABS_PROVIDER.SUPPLIERS.INDEX
            }
        });
    }


    return {
        user,
        setUser,
        FormHandler,
        resetForm,
        getAllServiceType,
        handleValidateSteps,
        handleOnBack
    };
}
